import type { ReactNode } from 'react';
import { ApiOutlined, ReloadOutlined, ShopOutlined } from '@ant-design/icons';

import { Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { maLoi, type ApiError } from '../../../lib/errors';

/**
 * Bốn trạng thái của một vùng dữ liệu, gom một chỗ vì năm màn của phân hệ đều cần y hệt (CLAUDE.md:
 * mọi vùng dữ liệu đủ bốn trạng thái).
 *
 * Có một trạng thái thứ năm riêng của phân hệ này: **route chưa mở**. Bốn màn của đợt sau gọi endpoint
 * backend chưa dựng và nhận `404`. Một câu "Không tìm thấy" ở đó là nói sai — người dùng không gõ nhầm
 * địa chỉ, tính năng chỉ chưa tới lượt. Nên `404` ở một màn đã khai `chuaMo` hiện lời giải thích riêng,
 * không hiện dải lỗi đỏ.
 */
export interface VungDuLieuProps<T> {
  /** Kết quả của hook TanStack Query. */
  dangTai: boolean;
  loi: ApiError | null;
  dulieu: T | undefined;
  /** Rỗng khi `dulieu` có nhưng không có hàng nào. */
  rong: boolean;
  /** `true` thì `404` hiểu là "đợt sau mới mở", không phải lỗi. */
  chuaMo?: boolean;
  /** Tiêu đề trạng thái rỗng; bỏ `rongMo` và `rongHanhDong` khi rỗng *vì bộ lọc*. */
  rongTieuDe: string;
  rongMo?: string;
  rongHanhDong?: ReactNode;
  rongIcon?: Parameters<typeof Empty>[0]['icon'];
  onThuLai: () => void;
  children: (du: T) => ReactNode;
}

export function VungDuLieu<T>({
  dangTai, loi, dulieu, rong, chuaMo, rongTieuDe, rongMo, rongHanhDong, rongIcon, onThuLai, children,
}: VungDuLieuProps<T>) {
  const { t } = useT();
  const dichLoi = useLoi();

  if (dangTai) {
    return (
      <div className="space-y-2 p-4" aria-busy="true">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    );
  }

  if (loi) {
    // Nhân sự nền tảng xem chéo giao diện tenant (ADR-UI-0012 mục 9): họ không thuộc tenant nào, nên
    // Gateway từ chối ở bước 3 với `TENANT_DOMAIN_NOT_VERIFIED` (domain `ops.platform.test` không
    // ánh xạ tenant nào) — hoặc, nếu tài khoản có domain của tenant mà gói chưa bật module, ở bước 8
    // với `PERMISSION_DENIED`. Cả hai đều không phải lỗi: nói đúng lý do thay vì "Đã có lỗi xảy ra",
    // vốn mời người dùng bấm Thử lại mãi mà không bao giờ khác đi.
    if (maLoi(loi) === 'TENANT_DOMAIN_NOT_VERIFIED' || maLoi(loi) === 'PERMISSION_DENIED') {
      return <Empty icon={ShopOutlined} title={t('thamDinh.xemCheoTieuDe')} description={t('thamDinh.xemCheoMo')} />;
    }
    if (chuaMo && loi.status === 404) {
      return <Empty icon={ApiOutlined} title={t('thamDinh.chuaMoTieuDe')} description={t('thamDinh.chuaMoMo')} />;
    }
    return (
      <div className="p-4">
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          <p>{dichLoi(loi)}</p>
          <Button variant="outline" size="sm" className="mt-3" icon={<ReloadOutlined />} onClick={onThuLai}>
            {t('chung.thuLai')}
          </Button>
        </div>
      </div>
    );
  }

  if (!dulieu || rong) {
    return <Empty icon={rongIcon} title={rongTieuDe} description={rongMo} action={rongHanhDong} />;
  }

  return <>{children(dulieu)}</>;
}
